import { Layers, Pencil, Star, Unlink } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { useQuoteAlternativesApi } from 'src/api/db/apiQuoteAlternatives'
import type { QuoteItem_ } from 'src/api/db/apiQuoteItem'
import { useOptionalRequestContext } from 'src/pages/_myAccount/Requests/Request/hooks/RequestContext'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Button, Input } from 'src/shared/components/ui'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/shared/components/ui/dialog'
import {
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from 'src/shared/components/ui/dropdown-menu'
import { useIsAdmin } from 'src/shared/hooks/useIsAdmin'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import {
  isBeforeCustomerApproval,
  planAddAlternative,
  planMarkRecommended,
  planRemoveAlternative,
  planRenameSlot,
  slotSiblings,
  slotTitleTaken,
} from 'src/shared/utils/quoteAlternatives'

const isProductLine = (item: QuoteItem_) =>
  item.type === 'product' || (!item.type && !!item.product)

type TitleDialog =
  | { mode: 'add'; targetId: number; title: string }
  | { mode: 'rename'; title: string }

/**
 * The card menu and Dialog run on different copies of Radix's dismissable
 * layer, so a dialog opened from the menu can restore the menu's
 * `pointer-events: none` on <body> when it closes, which freezes the page.
 * Once no dialog or menu is left open, drop that stale lock (gives up after
 * two seconds, e.g. if another modal stays open).
 */
const releaseStaleBodyLock = () => {
  const started = performance.now()
  const tick = () => {
    const layerOpen = document.querySelector('[role="dialog"], [role="menu"]')
    if (!layerOpen) {
      if (document.body.style.pointerEvents === 'none') {
        document.body.style.pointerEvents = ''
      }
    } else if (performance.now() - started < 2000) {
      requestAnimationFrame(tick)
    }
  }
  requestAnimationFrame(tick)
}

/**
 * Admin tools for one quote line on the request canvas: group it with another
 * line as alternatives, pick the recommended one, rename the line title the
 * customer sees (quote_item.slot), or take it out again. Staff only, and only
 * before the customer approves; everyone else gets nothing.
 */
export const useLineAlternatives = ({
  quoteItemId,
  isReadOnly,
}: {
  quoteItemId: number
  isReadOnly?: boolean
}) => {
  const { t } = useLocalization()
  const isAdmin = useIsAdmin()
  const request = useOptionalRequestContext()
  const { saveAlternatives } = useQuoteAlternativesApi()
  const [dialog, setDialog] = useState<TitleDialog | null>(null)
  const closeDialog = () => {
    setDialog(null)
    releaseStaleBodyLock()
  }

  const lines = (request?.quoteItems ?? []).filter(isProductLine)
  const line = lines.find((item) => item.id === quoteItemId)
  const visible = isAdmin && !request?.isDemo && !!line
  // Once the customer has approved (or picked), the choice and the order total
  // are recorded against these slots, so they stay as they are.
  const enabled =
    visible &&
    !isReadOnly &&
    isBeforeCustomerApproval(request?.quote?.status) &&
    !lines.some((item) => item.customer_selected_at)
  const slot = line?.slot?.trim() || null

  const nameOf = (item: QuoteItem_) =>
    item.product?.item_name ||
    (item.description ? t(item.description) : '') ||
    t('Product', 'Produkt')

  if (!visible || !line) {
    return { enabled: false, menuItems: null, dialog: null, badge: null }
  }

  // Lines already in this line's slot are its alternatives; rename covers them.
  const others = lines.filter(
    (item) => item.id !== quoteItemId && !(slot && item.slot?.trim() === slot)
  )
  const titleError = (title: string) => {
    if (!title.trim()) return t('Add a title', 'Lägg till en rubrik')
    const ownerId = dialog?.mode === 'add' ? dialog.targetId : quoteItemId
    if (slotTitleTaken(lines, title, ownerId))
      return t(
        'Another line already uses this title',
        'En annan rad har redan den här rubriken'
      )
    return null
  }

  const save = (updates: ReturnType<typeof planAddAlternative>) => {
    if (!updates.length) return
    saveAlternatives.mutate({ updates, lines })
  }

  const submitDialog = () => {
    if (!dialog || titleError(dialog.title)) return
    const updates =
      dialog.mode === 'add'
        ? planAddAlternative(lines, quoteItemId, dialog.targetId, dialog.title)
        : planRenameSlot(lines, quoteItemId, dialog.title)
    saveAlternatives.mutate(
      { updates, lines },
      { onSuccess: () => closeDialog() }
    )
  }

  const openAdd = (target: QuoteItem_) =>
    setDialog({
      mode: 'add',
      targetId: target.id,
      title: target.slot?.trim() || nameOf(target),
    })

  const menuItems: ReactNode = (
    <>
      <DropdownMenuSeparator />
      {others.length > 0 && (
        <DropdownMenuSub>
          <DropdownMenuSubTrigger className="gap-2">
            <Layers className="h-3.5 w-3.5" />
            {t('Add as alternative to', 'Lägg till som alternativ till')}
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="max-h-72 min-w-[200px] overflow-y-auto">
            {others.map((target) => (
              <DropdownMenuItem
                key={target.id}
                className="flex-col items-start gap-0"
                onClick={(e) => {
                  e.stopPropagation()
                  openAdd(target)
                }}
              >
                <span className="truncate">{nameOf(target)}</span>
                {target.slot?.trim() && (
                  <span className="text-xs text-muted-foreground">
                    {target.slot.trim()}
                  </span>
                )}
              </DropdownMenuItem>
            ))}
          </DropdownMenuSubContent>
        </DropdownMenuSub>
      )}
      {slot && !line.is_recommended && (
        <DropdownMenuItem
          className="gap-2"
          onClick={(e) => {
            e.stopPropagation()
            save(planMarkRecommended(lines, quoteItemId))
          }}
        >
          <Star className="h-3.5 w-3.5" />
          {t('Mark as recommended', 'Markera som rekommenderad')}
        </DropdownMenuItem>
      )}
      {slot && (
        <DropdownMenuItem
          className="gap-2"
          onClick={(e) => {
            e.stopPropagation()
            setDialog({ mode: 'rename', title: slot })
          }}
        >
          <Pencil className="h-3.5 w-3.5" />
          {t('Rename line', 'Byt namn på raden')}
        </DropdownMenuItem>
      )}
      {slot && (
        <DropdownMenuItem
          className="gap-2"
          onClick={(e) => {
            e.stopPropagation()
            save(planRemoveAlternative(lines, quoteItemId))
          }}
        >
          <Unlink className="h-3.5 w-3.5" />
          {t('Remove from alternatives', 'Ta bort från alternativ')}
        </DropdownMenuItem>
      )}
    </>
  )

  const siblings = slot ? slotSiblings(lines, quoteItemId).length + 1 : 0
  const badge: ReactNode = slot ? (
    <Row className="min-w-0 items-center gap-1">
      <Layers className="h-2.5 w-2.5 shrink-0 text-gray-400" />
      <p className="truncate text-[10px] text-gray-500">
        {slot} · {siblings} {t('options', 'alternativ')}
      </p>
      {line.is_recommended && (
        <span className="shrink-0 rounded bg-orange-50 px-1 text-[10px] font-medium text-orange-700">
          {t('Recommended', 'Rekommenderad')}
        </span>
      )}
    </Row>
  ) : null

  const error = dialog ? titleError(dialog.title) : null
  const dialogNode: ReactNode = (
    <Dialog open={!!dialog} onOpenChange={(open) => !open && closeDialog()}>
      <DialogContent
        className="max-w-md gap-4"
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        // React bubbles through the portal; keep Enter from opening the card.
        onKeyDown={(e) => e.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle>
            {dialog?.mode === 'rename'
              ? t('Rename line', 'Byt namn på raden')
              : t('Add as alternative', 'Lägg till som alternativ')}
          </DialogTitle>
          <DialogDescription>
            {t(
              'The customer sees this title above the options and picks one of them.',
              'Kunden ser rubriken ovanför alternativen och väljer ett av dem.'
            )}
          </DialogDescription>
        </DialogHeader>
        <Column className="gap-1.5">
          <Input
            autoFocus
            value={dialog?.title ?? ''}
            onChange={(e) =>
              setDialog((prev) =>
                prev ? { ...prev, title: e.target.value } : prev
              )
            }
            onKeyDown={(e) => {
              if (e.key === 'Enter') submitDialog()
            }}
            placeholder={t('e.g. Hoodie', 't.ex. Hoodie')}
            maxLength={60}
          />
          {error && dialog?.title !== '' && (
            <p className="text-xs text-red-600">{error}</p>
          )}
        </Column>
        <DialogFooter className="gap-2">
          <Button
            variant="secondary"
            onClick={() => closeDialog()}
            disabled={saveAlternatives.isPending}
          >
            {t('Cancel', 'Avbryt')}
          </Button>
          <Button
            onClick={submitDialog}
            disabled={!!error || saveAlternatives.isPending}
            isLoading={saveAlternatives.isPending}
          >
            {t('Save', 'Spara')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )

  // After approval the label stays (staff still see the groups), the tools go.
  return enabled
    ? { enabled: true, menuItems, dialog: dialogNode, badge }
    : { enabled: false, menuItems: null, dialog: null, badge }
}
